import FieldCard from "./FieldCard";
import type { VerifiableField, RuleSuggestion } from "../../api/client";

interface FieldCardGroupProps {
  section: string;
  fields: VerifiableField[];
  focusedPath: string | null;
  onVerify: (fieldPath: string) => void;
  onCorrect: (fieldPath: string, newValue: unknown) => void;
  onFocus: (fieldPath: string) => void;
  onFlag?: (fieldPath: string, notes?: string) => void;
  onRevert?: (fieldPath: string) => void;
  rulesApplied?: Record<string, string>;
  suggestions?: RuleSuggestion[];
  onApproveSuggestion?: (suggestion: RuleSuggestion) => void;
}

export default function FieldCardGroup({
  section,
  fields,
  focusedPath,
  onVerify,
  onCorrect,
  onFocus,
  onFlag,
  onRevert,
  rulesApplied,
  suggestions,
  onApproveSuggestion,
}: FieldCardGroupProps) {
  const allVerified = fields.every(
    (f) => f.state === "verified" || f.state === "corrected",
  );

  return (
    <div className="mb-6">
      <div
        className={`flex items-center gap-2 mb-3 px-2 py-1 rounded-md transition-all duration-300 ${
          allVerified ? "opacity-70 animate-sweep-complete" : ""
        }`}
      >
        <h3 className="font-display text-sm font-semibold text-bark-700">
          {section}
        </h3>
        {allVerified && (
          <span className="text-xs text-sage-600 font-medium">
            All verified
          </span>
        )}
      </div>
      <div className="grid gap-2">
        {fields.map((field) => (
          <FieldCard
            key={field.field_path}
            field={field}
            isFocused={focusedPath === field.field_path}
            onVerify={onVerify}
            onCorrect={onCorrect}
            onFocus={onFocus}
            onFlag={onFlag}
            onRevert={onRevert}
            ruleApplied={rulesApplied?.[field.field_path] ?? null}
            suggestion={
              suggestions?.find((s) => s.field_path === field.field_path) ?? null
            }
            onApproveSuggestion={onApproveSuggestion}
          />
        ))}
      </div>
    </div>
  );
}
